import { useTranslation } from "react-i18next";
import _ from "lodash";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import Icon from "../../../../Icon/Icon";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import {
  InvestigationCollapsable,
  Separator,
  UploadedFile,
} from "../components/InvestigationComponents";
import { transformDateAndHourToString } from "../helpers/formatDate";
import {
  ProposedActionDetails,
  ProposedActionTitle,
} from "./ProposedActionSummary";

export const SingleConclusion = ({
  conclusion,
  setIsEditing,
  handleSaveConclusionDetails,
  justView,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  // Indicador del resultado: comprobada / no comprobada / parcialmente
  const verifiedIndicator = {
    yes: { icon: "bold_tick_circle", color: theme.palette.green.main },
    no: { icon: "bold_close_circle", color: theme.palette.red.main },
    partially: { icon: "bold_minus_circle", color: theme.palette.orange.main },
  }[conclusion.verified];

  const conclusionDate = useMemo(() => {
    const [fecha, hora] = transformDateAndHourToString(
      conclusion.conclusion_at
    );
    return `${fecha ? fecha.toLocaleUpperCase() : t("Conclusion.not_scheduled")} ${
      hora ? " - " + hora.toLocaleLowerCase() : ""
    }`;
  }, [conclusion.conclusion_at]);

  const handleEditConclusion = () => {
    // setSelectedConclusion(idx);
    setIsEditing(true);
  };

  const handleRemoveConclusion = () => {
    handleSaveConclusionDetails({
      conclusion: conclusion.conclusion,
      comment: conclusion.comment,
      conclusion_at: null,
      folder: {
        conclusion: {
          files: [],
          measures: [],
        },
      },
    });
  };

  const actions = [
    {
      name: t("speeddial.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.secondary.main} />,
      onClick: handleEditConclusion,
    },
    // ...( conclusion.conclusion_at ? [
    //   {
    //     name: t("speeddial.delete"),
    //     icon: <Icon name="trash_can" color={theme.palette.error.main} />,
    //     onClick: handleRemoveConclusion,
    //   },
    // ] : []),
  ];

  // console.log("Conclusion");
  // console.log(conclusion);

  return (
    <EGAccordionSingle
      title={t("Conclusion.single_conclusion_title")}
      bgColor={"#7BBDFF"}
      color={theme.palette.white.main}
      actions={!justView ? actions : undefined}
    >
      <InvestigationItemContainer sx={{ p: 2 }} color={"#F26D6D"}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Typography color={theme.palette.blueGray.main}>
            {t("Conclusion.conclusion_date")}:&nbsp;
            <Typography component="span" fontWeight={700} color={"#F26D6D"}>
              {conclusionDate}
            </Typography>
          </Typography>

          {verifiedIndicator && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography color={theme.palette.blueGray.main}>
                {t("Conclusion.verified")}:
              </Typography>
              {/* El color del Icon sólo pinta la raíz del svg */}
              <Box
                display="flex"
                sx={{ "& svg, & svg *": { fill: verifiedIndicator.color } }}
              >
                <Icon name={verifiedIndicator.icon} size={20} />
              </Box>
              <Typography fontWeight={700} color={verifiedIndicator.color}>
                {t(`Conclusion.verified_${conclusion.verified}`)}
              </Typography>
            </Stack>
          )}
        </Stack>

        <hr className="hr-div" />

        <Grid2 container spacing={2}>
          <InvestigationBlockInfo
            label={t("Conclusion.conclusion_description")}
            text={conclusion.conclusion}
            richTextEditor={true}
            size={12}
          />
          {conclusion.comment && (
            <InvestigationBlockInfo
              label={t(
                "Conclusion.details_of_investigation_and_observer"
              )}
              text={conclusion.comment}
              size={12}
            />
          )}
        </Grid2>
      </InvestigationItemContainer>

      {conclusion.files.length > 0 && (
        <>
          <Separator
            text={t("Conclusion.support_files_for_the_conclusion")}
            sx={{ my: 2 }}
          />
          <Grid2 container spacing={2}>
            {Array.isArray(conclusion.files) &&
              conclusion.files.map((file, idx) => (
                <Grid2 size={{ xs: 12, lg: 6 }} key={idx}>
                  <UploadedFile
                    text={file.name}
                    description={file.details}
                    downloadUrl={file.url}
                    width="100%"
                  />
                </Grid2>
              ))}
          </Grid2>
        </>
      )}

      {Array.isArray(conclusion.measures) && conclusion.measures.length > 0 && (
        <>
          <Separator
            text={t(
              "Conclusion.measures_corrective_description"
            )}
            sx={{ my: 2 }}
          />
          <Stack spacing={2}>
            {conclusion.measures.map((measure, idx) => (
              <InvestigationCollapsable
                key={idx}
                title={<ProposedActionTitle measure={measure} />}
                details={<ProposedActionDetails measure={measure} />}
              />
            ))}
          </Stack>
        </>
      )}
    </EGAccordionSingle>
  );
};
